import React, { useState, useEffect, useRef, useMemo } from 'react';
import './Contact.css';
import { useLanguage } from '../contexts/LanguageContext';
import { CreditCardIcon, UsersIcon } from './Icons';
import { useBusinessTypes } from '../hooks/useBusinessTypes';
import { useTariffs } from '../hooks/useTariffs';
import { createOrder } from '../services/ordersApi';
import { Select } from './ui/select';

const Contact = () => {
  const { t } = useLanguage();
  const { list: businessTypesList = [] } = useBusinessTypes();
  const { data: tariffsList = [] } = useTariffs();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    businessTypeId: '',
    tariffId: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null); // 'sending' | 'success' | 'error'
  const [submitError, setSubmitError] = useState('');
  
  const safeBusinessTypesList = Array.isArray(businessTypesList) ? businessTypesList : [];
  const safeTariffsList = Array.isArray(tariffsList) ? tariffsList : [];
  
  // Barcha tariflarni ko'rsatish — API dan kelgan barcha elementlar
  const filteredTariffsList = useMemo(() => {
    return Array.isArray(safeTariffsList) ? safeTariffsList.filter((tariff) => tariff && tariff.id) : [];
  }, [safeTariffsList]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('sending');
    setSubmitError('');
    try {
      const payload = {
        clientName: formData.name,
        clientPhone: formData.phone,
        source: 'site',
        status: 'new',
      };
      if (formData.company?.trim()) payload.company = formData.company.trim();
      if (formData.businessTypeId) payload.businessTypeId = formData.businessTypeId;
      if (formData.tariffId) payload.tariffId = formData.tariffId;
      if (formData.message?.trim()) {
        payload.note = formData.message.trim();
        payload.message = formData.message.trim();
      }
      await createOrder(payload);
      setSubmitStatus('success');
      setFormData({ name: '', phone: '', company: '', businessTypeId: '', tariffId: '', message: '' });
    } catch (err) {
      setSubmitStatus('error');
      setSubmitError(err?.message || t('contactSubmitError') || 'Xabar yuborilmadi.');
    }
  };

  const mapRef = useRef(null);

  // Bog'lanish sahifasiga kirilganda xabar yuborish formasiga scroll
  useEffect(() => {
    const formEl = document.getElementById('contact-form');
    if (formEl) {
      const timeoutId = setTimeout(() => {
        formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  useEffect(() => {
    // Capture current ref value for cleanup
    const currentMapRef = mapRef.current;
    
    // Yandex Maps initialization
    const initMap = () => {
      if (window.ymaps && mapRef.current && !mapRef.current.ymapsMap) {
        window.ymaps.ready(() => {
          if (mapRef.current && !mapRef.current.ymapsMap) {
            try {
              const map = new window.ymaps.Map(mapRef.current, {
                center: [41.3111, 69.2797], // Toshkent coordinates
                zoom: 15,
                controls: ['zoomControl', 'fullscreenControl']
              });

              // Add marker
              const marker = new window.ymaps.Placemark(
                [41.3111, 69.2797],
                {
                  balloonContent: t('contactOfficeAddress')
                },
                {
                  preset: 'islands#blueIcon'
                }
              );

              map.geoObjects.add(marker);
              mapRef.current.ymapsMap = map;

              // Konteyner o‘lchami o‘zgaganda yoki to‘liq ekrandan chiqganda xarita va divlar default holatga qaytsin
              const containerEl = mapRef.current;
              const resetContactLayout = () => {
                const layoutProps = ['position', 'left', 'right', 'top', 'bottom', 'width', 'height', 'max-width', 'min-width', 'transform', 'margin', 'margin-left', 'margin-right'];
                let el = containerEl;
                while (el && !el.classList?.contains('contact-section')) {
                  layoutProps.forEach((prop) => {
                    try {
                      el.style.removeProperty(prop);
                    } catch (e) {}
                  });
                  el = el.parentElement;
                }
              };
              const fitViewport = () => {
                try {
                  const mapInstance = mapRef.current?.ymapsMap;
                  if (!mapInstance) return;
                  if (containerEl && containerEl.parentElement) {
                    const parent = containerEl.parentElement;
                    containerEl.style.width = '100%';
                    containerEl.style.height = '100%';
                    containerEl.style.minHeight = '0';
                    containerEl.style.maxWidth = '';
                    containerEl.style.position = '';
                    containerEl.style.left = '';
                    containerEl.style.right = '';
                    containerEl.style.top = '';
                    containerEl.style.bottom = '';
                    void parent.offsetHeight;
                  }
                  mapInstance.container.fitToViewport();
                } catch (e) {
                  // ignore
                }
              };
              const fitViewportAfterFullscreen = () => {
                resetContactLayout();
                [0, 80, 200, 400].forEach((delay) => setTimeout(fitViewport, delay));
              };
              // To‘liq ekrandan chiqganda xaritani avtoyangilash: destroy + qayta yaratish
              const refreshMapAfterFullscreen = () => {
                resetContactLayout();
                const runCleanup = mapRef.current?._mapCleanup;
                const mapInstance = mapRef.current?.ymapsMap;
                if (runCleanup) runCleanup();
                if (mapInstance) {
                  try {
                    mapInstance.destroy();
                  } catch (e) {}
                  if (mapRef.current) mapRef.current.ymapsMap = null;
                }
                setTimeout(() => initMap(), 120);
              };
              // Dastlabki layout dan keyin bir marta
              const initialFit = setTimeout(fitViewport, 100);
              const resizeObserver = new ResizeObserver(() => {
                clearTimeout(resizeDebounce);
                resizeDebounce = setTimeout(fitViewport, 80);
              });
              let resizeDebounce;
              if (containerEl) resizeObserver.observe(containerEl);
              // Yandex xaritasining to‘liq ekrandan chiqish hodisasi — avtoyangilash
              let fullscreenExitHandler;
              try {
                fullscreenExitHandler = () => refreshMapAfterFullscreen();
                map.container.events.add('fullscreenexit', fullscreenExitHandler);
              } catch (err) {
                // API farq qilishi mumkin
              }
              // Brauzer fullscreen (fallback) — avtoyangilash
              const onFullscreenChange = () => {
                if (!document.fullscreenElement && !document.webkitFullscreenElement) {
                  refreshMapAfterFullscreen();
                }
              };
              document.addEventListener('fullscreenchange', onFullscreenChange);
              document.addEventListener('webkitfullscreenchange', onFullscreenChange);
              mapRef.current._mapCleanup = () => {
                clearTimeout(initialFit);
                clearTimeout(resizeDebounce);
                resizeObserver.disconnect();
                try {
                  if (mapRef.current?.ymapsMap?.container?.events && fullscreenExitHandler) {
                    mapRef.current.ymapsMap.container.events.remove('fullscreenexit', fullscreenExitHandler);
                  }
                } catch (e) {}
                document.removeEventListener('fullscreenchange', onFullscreenChange);
                document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
              };
            } catch (error) {
              console.error('Yandex Maps initialization error:', error);
            }
          }
        });
      }
    };

    if (window.ymaps) {
      initMap();
    } else {
      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="api-maps.yandex.ru"]');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://api-maps.yandex.ru/2.1/?lang=ru_RU';
        script.async = true;
        script.onload = initMap;
        script.onerror = () => {
          console.error('Failed to load Yandex Maps');
        };
        document.head.appendChild(script);
      } else {
        existingScript.addEventListener('load', initMap);
        if (window.ymaps) {
          initMap();
        }
      }
    }

    return () => {
      if (currentMapRef) {
        if (typeof currentMapRef._mapCleanup === 'function') {
          currentMapRef._mapCleanup();
        }
        if (currentMapRef.ymapsMap) {
          try {
            currentMapRef.ymapsMap.destroy();
          } catch (error) {
            console.error('Error destroying map:', error);
          }
          currentMapRef.ymapsMap = null;
        }
      }
    };
  }, [t]);

  const contactMethods = [
    {
      icon: CreditCardIcon,
      titleKey: 'contactPhone',
      value: '+998 90 123 45 67',
      action: 'tel:+998901234567'
    },
    {
      icon: UsersIcon,
      titleKey: 'contactOffice',
      value: 'Toshkent, O\'zbekiston',
      action: null
    }
  ];

  // Fallback for translation function
  const safeT = (key, fallback = key) => {
    try {
      return t ? t(key) || fallback : fallback;
    } catch (err) {
      console.error('Translation error:', err);
      return fallback;
    }
  };

  return (
    <section className="contact-section" id="contact">
      <div className="contact-background-decoration"></div>
      <div className="container">
        <div className="contact-header">
          <h2 className="section-title contact-title">{safeT('sectionContact', 'Bog\'lanish')}</h2>
          <p className="section-subtitle contact-subtitle">
            {safeT('contactSubtitle', 'Biz bilan bog\'laning')}
          </p>
        </div>
        <div className="contact-content">
          <div className="contact-info">
            <div className="contact-methods">
              {contactMethods.map((method, index) => {
                const IconComponent = method.icon;
                return (
                  <a
                    key={index}
                    href={method.action || '#'}
                    className="contact-method-card"
                    onClick={method.action ? null : (e) => e.preventDefault()}
                  >
                    <div className="contact-method-icon">
                      <IconComponent size={24} />
                    </div>
                    <div className="contact-method-content">
                      <div className="contact-method-title">{t(method.titleKey)}</div>
                      <div className="contact-method-value">{method.value}</div>
                    </div>
                  </a>
                );
              })}
            </div>
            <div className="contact-map-wrapper">
              <div className="contact-map-container">
                <div ref={mapRef} className="yandex-map" id="yandex-map"></div>
              </div>
              <div className="contact-address">
                <p className="address-text">{t('contactOfficeAddress')}</p>
              </div>
            </div>
          </div>
          <div className="contact-form-wrapper" id="contact-form">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">{t('formName')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={t('formNamePlaceholder')}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">{t('formPhone')}</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={t('formPhonePlaceholder')}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">{t('formCompany')}</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder={t('formCompanyPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="businessTypeId">
                    {t('formBusinessType')}
                    <span className="contact-label-optional"> ({t('formOptional') || 'ixtiyoriy'})</span>
                  </label>
                  <Select
                    id="businessTypeId"
                    name="businessTypeId"
                    value={formData.businessTypeId}
                    onChange={(v) => setFormData((prev) => ({ ...prev, businessTypeId: v }))}
                    className="contact-select"
                    options={[
                      { value: '', label: t('formBusinessTypePlaceholder') },
                      ...safeBusinessTypesList.map((bt) => ({
                        value: String(bt.id),
                        label: bt.name || (bt.nameKey ? t(bt.nameKey) : String(bt.id)),
                      })),
                    ]}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="tariffId">
                  {t('formTariff') || 'Tarif'}
                  <span className="contact-label-optional"> ({t('formOptional') || 'ixtiyoriy'})</span>
                </label>
                <Select
                  id="tariffId"
                  name="tariffId"
                  value={formData.tariffId}
                  onChange={(v) => setFormData((prev) => ({ ...prev, tariffId: v }))}
                  className="contact-select"
                  options={[
                    { value: '', label: t('formTariffPlaceholder') || 'Tarifni tanlang' },
                    ...filteredTariffsList.map((tariff) => {
                      const tariffName = tariff.customName || (tariff.typeKey ? t(tariff.typeKey) : `Tarif ${tariff.id}`);
                      return { value: String(tariff.id), label: tariffName };
                    }),
                  ]}
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">
                  {t('formMessage')}
                  <span className="contact-label-optional"> ({t('formOptional') || 'ixtiyoriy'})</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('formMessagePlaceholder')}
                  rows="5"
                ></textarea>
              </div>
              {submitStatus === 'success' && (
                <p className="contact-form-success" role="alert">
                  {t('contactSubmitSuccess') || 'Xabaringiz qabul qilindi. Tez orada siz bilan bog\'lanamiz.'}
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="contact-form-error" role="alert">
                  {submitError}
                </p>
              )}
              <button type="submit" className="btn-primary btn-large contact-submit" disabled={submitStatus === 'sending'}>
                {submitStatus === 'sending' ? (t('contactSending') || 'Yuborilmoqda…') : t('btnSendMessage')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;

